package com.arena_web.data.websocket

import com.arena_web.core.config.AppConfig
import com.arena_web.core.logger.getLogger
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import org.w3c.dom.WebSocket
import org.w3c.dom.events.Event

/** Optional string field — `JsonNull` (whose `.content` is the literal string
 *  "null") and blank values both count as absent. */
private fun JsonObject.optString(key: String): String? =
    (this[key] as? JsonPrimitive)?.contentOrNull?.takeIf { it.isNotBlank() }

sealed class UserWsEvent {
    data class ChatListUpdate(
        val chatId: String,
        /** Display name of the message author + short preview — present on
         *  new-message updates so the UI can show a "who wrote what" banner. */
        val senderName: String? = null,
        val preview: String? = null,
        val senderAvatarUrl: String? = null,
        val senderCardAssetsBasePath: String? = null,
    ) : UserWsEvent()
    data class NewNotification(
        val title: String,
        val body: String? = null,
        val notificationType: String? = null,
        /** Actor visuals parsed from the notification's dataJson — same fields
         *  the notifications panel uses for its player-card avatar. */
        val actorAvatarUrl: String? = null,
        val actorCardAssetsBasePath: String? = null,
    ) : UserWsEvent()
    data object ActivityUpdate : UserWsEvent()
    /**
     * Header heart-badge signal — fires when someone LIKEd the caller or a
     * mutual like turned into a match. `kind` is either "LIKE" or "MATCH".
     */
    data class InteractionsUpdate(val kind: String, val actorUserId: String?) : UserWsEvent()
    data class UserOnline(val userId: String) : UserWsEvent()
    data class UserOffline(val userId: String) : UserWsEvent()
    data class OnlineUsersList(val userIds: Set<String>) : UserWsEvent()
    data object Connected : UserWsEvent()
    data object Disconnected : UserWsEvent()
}

class UserWebSocketClient {
    private val logger = getLogger<UserWebSocketClient>()
    private val json = Json { ignoreUnknownKeys = true; isLenient = true }

    private var webSocket: WebSocket? = null
    private var reconnectAttempts = 0
    private var reconnectTimerId: Int? = null
    private var intentionalDisconnect = false

    private val _events = MutableSharedFlow<UserWsEvent>(extraBufferCapacity = 64)
    val events: SharedFlow<UserWsEvent> = _events.asSharedFlow()

    fun connect() {
        disconnect()
        intentionalDisconnect = false

        val token = Storage.get(StorageKeys.ACCESS_TOKEN) ?: run {
            logger.error("No access token for User WebSocket connection")
            return
        }

        val url = "${AppConfig.wsUrl}/user?token=$token"
        logger.debug("User WS connecting to $url")

        try {
            val ws = WebSocket(url)

            ws.onopen = {
                logger.debug("User WS connected")
                reconnectAttempts = 0
                _events.tryEmit(UserWsEvent.Connected)
                Unit
            }

            ws.onmessage = { event: Event ->
                val msgEvent = event.asDynamic()
                val text = msgEvent.data as? String
                if (text != null) {
                    handleMessage(text)
                }
                Unit
            }

            ws.onclose = {
                logger.debug("User WS disconnected")
                _events.tryEmit(UserWsEvent.Disconnected)
                if (!intentionalDisconnect) {
                    scheduleReconnect()
                }
                Unit
            }

            ws.onerror = {
                logger.error("User WS error")
                Unit
            }

            webSocket = ws
        } catch (e: Exception) {
            logger.error("Failed to create User WebSocket: ${e.message}")
        }
    }

    fun disconnect() {
        intentionalDisconnect = true
        reconnectTimerId?.let { kotlinx.browser.window.clearTimeout(it) }
        reconnectTimerId = null
        reconnectAttempts = 0
        webSocket?.close()
        webSocket = null
    }

    private fun scheduleReconnect() {
        if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
            logger.error("User WS max reconnect attempts reached")
            return
        }

        val delayMs = RECONNECT_DELAYS.getOrElse(reconnectAttempts) { RECONNECT_DELAYS.last() }
        reconnectAttempts++
        logger.debug("User WS reconnecting in ${delayMs}ms (attempt $reconnectAttempts)")

        reconnectTimerId = kotlinx.browser.window.setTimeout({
            reconnectTimerId = null
            if (!intentionalDisconnect) {
                connect()
            }
        }, delayMs)
    }

    private fun handleMessage(text: String) {
        try {
            val jsonElement = json.parseToJsonElement(text)
            val obj = jsonElement.jsonObject
            val type = obj["type"]?.jsonPrimitive?.content ?: return
            val data = obj["data"]

            val event = when (type) {
                "CHAT_LIST_UPDATE" -> {
                    val obj = data?.jsonObject ?: return
                    val chatId = obj["chatId"]?.jsonPrimitive?.content ?: return
                    UserWsEvent.ChatListUpdate(
                        chatId = chatId,
                        senderName = obj.optString("senderName"),
                        preview = obj.optString("preview"),
                        senderAvatarUrl = obj.optString("senderAvatarUrl"),
                        senderCardAssetsBasePath = obj.optString("senderCardAssetsBasePath"),
                    )
                }
                "NEW_NOTIFICATION" -> {
                    val obj = data?.jsonObject
                    val title = obj?.optString("title")
                    // dataJson is a nested JSON string with the actor payload
                    // (same shape NotificationDto.parseDataJson handles).
                    val payload = obj?.optString("dataJson")
                        ?.let { raw ->
                            try {
                                json.parseToJsonElement(raw).jsonObject
                            } catch (_: Exception) {
                                null
                            }
                        }
                    if (title != null) {
                        UserWsEvent.NewNotification(
                            title = title,
                            body = obj?.optString("body"),
                            notificationType = obj?.optString("type"),
                            actorAvatarUrl = payload?.optString("actorAvatarUrl"),
                            actorCardAssetsBasePath = payload?.optString("actorCardAssetsBasePath"),
                        )
                    } else null
                }
                "ACTIVITY_UPDATE" -> UserWsEvent.ActivityUpdate
                "INTERACTIONS_UPDATE" -> {
                    val kind = data?.jsonObject?.get("kind")?.jsonPrimitive?.content ?: "LIKE"
                    val actorUserId = data?.jsonObject?.get("actorUserId")?.jsonPrimitive?.content
                    UserWsEvent.InteractionsUpdate(kind = kind, actorUserId = actorUserId)
                }
                "USER_ONLINE" -> {
                    val userId = data?.jsonObject?.get("userId")?.jsonPrimitive?.content ?: return
                    UserWsEvent.UserOnline(userId)
                }
                "USER_OFFLINE" -> {
                    val userId = data?.jsonObject?.get("userId")?.jsonPrimitive?.content ?: return
                    UserWsEvent.UserOffline(userId)
                }
                "ONLINE_USERS_LIST" -> {
                    val userIds = data?.jsonObject?.get("userIds")?.jsonArray
                        ?.mapNotNull { it.jsonPrimitive.content }
                        ?.toSet() ?: emptySet()
                    UserWsEvent.OnlineUsersList(userIds)
                }
                else -> {
                    logger.debug("Unknown User WS event type: $type")
                    null
                }
            }

            if (event != null) {
                _events.tryEmit(event)
            }
        } catch (e: Exception) {
            logger.error("Failed to parse User WS message: ${e.message}")
        }
    }

    companion object {
        private const val MAX_RECONNECT_ATTEMPTS = 5
        private val RECONNECT_DELAYS = listOf(1000, 2000, 3000, 3000, 3000)
    }
}
